import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import {
  getLaunchSplashKind,
  type LaunchSplashKind,
} from '@/utils/platformUtils';

/** Solid black behind splash media; matches tabstr-launch.png edges / native splash. */
const LAUNCH_SPLASH_BACKGROUND = '#000000';
/** Safety cap if video `onEnded` never fires. Clip is ~4s. */
export const LAUNCH_SPLASH_TIMEOUT_MS = 5000;
/**
 * If the video never becomes playable (slow/offline cache miss), dismiss instead of sitting on black.
 * Electron/Capacitor usually already have the file local and hit `canplay` well before this.
 */
export const LAUNCH_SPLASH_BUFFER_WATCHDOG_MS = 1500;
/** How long the static iOS/iPad image splash stays up. */
export const LAUNCH_SPLASH_IMAGE_DURATION_MS = 1500;
/** Safety cap if the static image never loads. */
export const LAUNCH_SPLASH_IMAGE_TIMEOUT_MS = 3000;

type VideoLaunchSplashProps = {
  children: ReactNode;
};

function splashAssetUrl(path: string): string {
  const base = import.meta.env.BASE_URL;
  return base.endsWith('/') ? `${base}${path}` : `${base}/${path}`;
}

function splashMp4Url(): string {
  return splashAssetUrl('splash/launch.mp4');
}

function splashImageUrl(): string {
  return splashAssetUrl('splash/tabstr-launch.png');
}

export function VideoLaunchSplash({ children }: VideoLaunchSplashProps) {
  const [kind] = useState<LaunchSplashKind>(() => {
    if (typeof window === 'undefined') return 'none';
    return getLaunchSplashKind();
  });
  const [dismissed, setDismissed] = useState(() => kind === 'none');

  const videoRef = useRef<HTMLVideoElement>(null);
  const doneRef = useRef(false);
  const playStartedRef = useRef(false);

  const finish = useCallback(() => {
    if (doneRef.current) return;
    doneRef.current = true;
    setDismissed(true);
  }, []);

  /**
   * Prefer unmuted playback (Electron / Capacitor / Android PWA).
   * If autoplay is blocked, retry muted once, then dismiss.
   */
  const tryPlayOnce = useCallback(() => {
    const video = videoRef.current;
    if (!video || playStartedRef.current || doneRef.current) return;
    playStartedRef.current = true;

    const attemptPlay = (muted: boolean) => {
      video.muted = muted;
      video.defaultMuted = muted;
      if (muted) {
        video.setAttribute('muted', '');
      }
      return video.play();
    };

    void attemptPlay(false).catch(() => {
      if (doneRef.current) return;
      void attemptPlay(true).catch(() => finish());
    });
  }, [finish]);

  // Video: safety timeout
  useEffect(() => {
    if (dismissed || kind !== 'video') return;
    const t = window.setTimeout(finish, LAUNCH_SPLASH_TIMEOUT_MS);
    return () => window.clearTimeout(t);
  }, [dismissed, finish, kind]);

  // Video: buffer watchdog
  useEffect(() => {
    if (dismissed || kind !== 'video') return;
    const t = window.setTimeout(() => {
      if (doneRef.current || playStartedRef.current) return;
      const v = videoRef.current;
      if (!v || v.readyState < HTMLMediaElement.HAVE_FUTURE_DATA) {
        finish();
      }
    }, LAUNCH_SPLASH_BUFFER_WATCHDOG_MS);
    return () => window.clearTimeout(t);
  }, [dismissed, finish, kind]);

  // Video: if already buffered, canplay may have fired before onCanPlay was attached
  useEffect(() => {
    if (dismissed || kind !== 'video') return;
    const id = requestAnimationFrame(() => {
      const v = videoRef.current;
      if (v && v.readyState >= HTMLMediaElement.HAVE_FUTURE_DATA) {
        tryPlayOnce();
      }
    });
    return () => cancelAnimationFrame(id);
  }, [dismissed, kind, tryPlayOnce]);

  useEffect(() => {
    if (dismissed || kind !== 'video') return;
    const video = videoRef.current;
    if (!video) return;
    video.setAttribute('webkit-playsinline', '');
    video.setAttribute('playsinline', '');
  }, [dismissed, kind]);

  // Image (iOS/iPad PWA): hold briefly, then dismiss
  useEffect(() => {
    if (dismissed || kind !== 'image') return;
    const t = window.setTimeout(finish, LAUNCH_SPLASH_IMAGE_DURATION_MS);
    return () => window.clearTimeout(t);
  }, [dismissed, finish, kind]);

  // Image: hard safety if something goes wrong with the duration timer
  useEffect(() => {
    if (dismissed || kind !== 'image') return;
    const t = window.setTimeout(finish, LAUNCH_SPLASH_IMAGE_TIMEOUT_MS);
    return () => window.clearTimeout(t);
  }, [dismissed, finish, kind]);

  if (dismissed) {
    return <>{children}</>;
  }

  return (
    <>
      {children}
      <div
        className="fixed inset-0 z-9999 flex items-center justify-center overflow-hidden"
        style={{ backgroundColor: LAUNCH_SPLASH_BACKGROUND }}
        aria-hidden
        data-testid="video-launch-splash"
        data-splash-kind={kind}
      >
        {kind === 'image' ? (
          <img
            src={splashImageUrl()}
            alt=""
            className="h-full w-full object-contain"
            style={{ backgroundColor: LAUNCH_SPLASH_BACKGROUND }}
            decoding="async"
            onError={finish}
          />
        ) : (
          <video
            ref={videoRef}
            className="h-full w-full object-cover"
            style={{
              backgroundColor: LAUNCH_SPLASH_BACKGROUND,
              objectFit: 'cover',
            }}
            src={splashMp4Url()}
            poster="data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7"
            playsInline
            preload="auto"
            disablePictureInPicture
            disableRemotePlayback
            x-webkit-airplay="deny"
            onEnded={finish}
            onError={finish}
            onCanPlay={tryPlayOnce}
          />
        )}
      </div>
    </>
  );
}
